import { useState, useRef, useEffect } from 'react';
import { Calendar as CalendarIcon } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { formatItalianDateInput, parseItalianDateInput } from '@/utils/date';
import { format, startOfMonth, endOfMonth, startOfWeek, endOfWeek, eachDayOfInterval, isSameMonth, isToday, isSameDay, addMonths, subMonths, getDay } from 'date-fns';
import { it } from 'date-fns/locale';
import { cn } from '@/lib/utils';

interface DatePickerItalianProps {
  value: string; // Formato GG MM AAAA
  onChange: (value: string) => void; // Restituisce formato GG MM AAAA
  id?: string;
  min?: Date;
  max?: Date;
  className?: string;
  placeholder?: string;
  required?: boolean;
}

export function DatePickerItalian({
  value,
  onChange,
  id,
  min,
  max,
  className,
  placeholder,
  required,
}: DatePickerItalianProps) {
  const [open, setOpen] = useState(false);
  const [currentMonth, setCurrentMonth] = useState(new Date());

  // Converte GG MM AAAA in Date
  const getDateFromValue = (): Date | null => {
    if (!value || value.trim() === '') return null;
    return parseItalianDateInput(value);
  };

  // Gestisce la selezione di una data dal calendario
  const handleDateSelect = (date: Date) => {
    // Verifica min/max
    if (min && date < min) return;
    if (max && date > max) return;
    
    const formatted = formatItalianDateInput(date);
    onChange(formatted);
    setOpen(false);
  };

  // Gestisce l'input manuale
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let val = e.target.value;
    // Rimuovi caratteri non numerici e spazi eccessivi
    val = val.replace(/[^\d\s]/g, '');
    // Limita a 10 caratteri (DD MM YYYY)
    if (val.length <= 10) {
      // Formatta automaticamente: aggiungi spazi dopo 2 e 5 caratteri
      let formatted = val.replace(/\s/g, '');
      if (formatted.length > 2) {
        formatted = formatted.slice(0, 2) + ' ' + formatted.slice(2);
      }
      if (formatted.length > 5) {
        formatted = formatted.slice(0, 5) + ' ' + formatted.slice(5);
      }
      onChange(formatted);
      
      // Aggiorna il mese visualizzato se la data è valida
      if (formatted.length === 10) {
        const date = parseItalianDateInput(formatted);
        if (date) {
          setCurrentMonth(date);
        }
      }
    }
  };

  // Aggiorna currentMonth quando cambia value
  useEffect(() => {
    const date = getDateFromValue();
    if (date) {
      setCurrentMonth(date);
    }
  }, [value]);

  const selectedDate = getDateFromValue();
  const monthStart = startOfMonth(currentMonth);
  const monthEnd = endOfMonth(currentMonth);
  const calendarStart = startOfWeek(monthStart, { weekStartsOn: 1 });
  const calendarEnd = endOfWeek(monthEnd, { weekStartsOn: 1 });
  const calendarDays = eachDayOfInterval({ start: calendarStart, end: calendarEnd });

  const weekDays = ['L', 'M', 'M', 'G', 'V', 'S', 'D'];

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <div className="relative">
        <Input
          id={id}
          type="text"
          value={value || ''}
          onChange={handleInputChange}
          onClick={() => setOpen(true)}
          onFocus={() => setOpen(true)}
          placeholder={placeholder || "GG MM AAAA"}
          maxLength={10}
          className={cn("pr-8", className)}
          required={required}
        />
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7 p-0 hover:bg-transparent"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setOpen(!open);
            }}
          >
            <CalendarIcon className="h-4 w-4 text-gray-500" />
          </Button>
        </PopoverTrigger>
      </div>
      <PopoverContent className="w-auto p-0" align="start" onOpenAutoFocus={(e) => e.preventDefault()}>
        <div className="p-3">
          {/* Header con navigazione mesi */}
          <div className="flex items-center justify-between mb-4">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => setCurrentMonth(subMonths(currentMonth, 1))}
            >
              ←
            </Button>
            <div className="font-semibold">
              {format(currentMonth, 'MMMM yyyy', { locale: it })}
            </div>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => setCurrentMonth(addMonths(currentMonth, 1))}
            >
              →
            </Button>
          </div>

          {/* Intestazione giorni settimana */}
          <div className="grid grid-cols-7 gap-1 mb-2">
            {weekDays.map((day, index) => (
              <div key={index} className="text-center text-sm font-semibold text-gray-600 py-2 w-10">
                {day}
              </div>
            ))}
          </div>

          {/* Griglia calendario */}
          <div className="grid grid-cols-7 gap-1">
            {calendarDays.map((day) => {
              const isCurrentMonth = isSameMonth(day, currentMonth);
              const isDayToday = isToday(day);
              const isSelected = selectedDate && isSameDay(day, selectedDate);
              const isDisabled = (min && day < min) || (max && day > max);

              return (
                <button
                  key={day.toISOString()}
                  type="button"
                  onClick={() => !isDisabled && handleDateSelect(day)}
                  disabled={isDisabled}
                  className={cn(
                    "h-10 w-10 rounded-md text-sm transition-colors",
                    isCurrentMonth ? "text-gray-900" : "text-gray-400",
                    isDayToday && "bg-blue-100 font-semibold",
                    isSelected && "bg-blue-600 text-white hover:bg-blue-700",
                    !isSelected && !isDayToday && "hover:bg-gray-100",
                    isDisabled && "opacity-50 cursor-not-allowed"
                  )}
                >
                  {format(day, 'd')}
                </button>
              );
            })}
          </div>

          {/* Pulsanti Clear e Today */}
          <div className="flex justify-between mt-4 pt-4 border-t">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => {
                onChange('');
                setOpen(false);
              }}
            >
              Clear
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => {
                const today = new Date();
                if ((!min || today >= min) && (!max || today <= max)) {
                  handleDateSelect(today);
                }
              }}
            >
              Today
            </Button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
